"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { m as motion } from "framer-motion";
import {
  ArrowRight,
  TrendingUp,
  Zap,
  Target,
  BarChart3,
  Users,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { LANDING_STATS, LANDING_ANIMATIONS } from "@/lib/landing-constants";
import { trackMarketingCtaClick } from "@/utils/analytics";

export type OptimizerLandingVariant = "optimizer" | "props";

interface OptimizerLandingProps {
  variant: OptimizerLandingVariant;
}

const VARIANT_CONTENT = {
  optimizer: {
    title: "Optimizer",
    subtitle:
      "Every spread, total, moneyline and player prop at your sportsbook, priced against the market so you can see where the math is on your side.",
    description:
      "The Optimizer scans 30+ sportsbooks in real time and shows you where your book's price beats the market. Game lines and player props on one board, sorted by edge.",
    features: [
      {
        icon: TrendingUp,
        title: "True Fair Value",
        description:
          "See the real odds stripped of vig. Know exactly when a line has value.",
      },
      {
        icon: Zap,
        title: "Sub-5 Second Updates",
        description:
          "Lines move fast. Our scanner updates faster. Never miss an edge.",
      },
      {
        icon: Target,
        title: "Multi-Book Comparison",
        description:
          "Compare game lines and player props across 30+ books in one view.",
      },
      {
        icon: BarChart3,
        title: "EV Calculations",
        description:
          "Instant expected value calculations show your exact mathematical edge.",
      },
    ],
    screenshot: "/images/landing/ev-gamelines.webp",
    screenshotAlt: "Optimizer Dashboard",
    cta: "Start Finding Edges",
    analyticsVariant: "optimizer",
  },
  props: {
    title: "Fantasy Props",
    subtitle:
      "Compare DFS prop lines and odds-to-hit across fantasy platforms before you build a slip.",
    description:
      "The player props market moves fast. Upside scans fantasy platforms and fair-value sources so you can see which props have the best probability and price context before markets move.",
    features: [
      {
        icon: Users,
        title: "Multi-Platform Coverage",
        description:
          "Compare props from PrizePicks, Underdog, and more in one place.",
      },
      {
        icon: Target,
        title: "Sharp Line Consensus",
        description:
          "True probability calculations from the sharpest books available.",
      },
      {
        icon: Clock,
        title: "Real-Time Updates",
        description:
          "Props change constantly. Stay ahead with sub-5 second refreshes.",
      },
      {
        icon: BarChart3,
        title: "Hit Rate Analysis",
        description:
          "See consensus odds-to-hit for every prop play you consider.",
      },
    ],
    screenshot: "/images/landing/ev-gamelines.webp",
    screenshotAlt: "Fantasy Props Dashboard",
    cta: "Start 5-day trial",
    analyticsVariant: "props_optimizer",
  },
} as const;

export function OptimizerLanding({ variant }: OptimizerLandingProps) {
  const content = VARIANT_CONTENT[variant];

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    if (img.dataset.fallbackApplied === "true") return;
    img.dataset.fallbackApplied = "true";
    img.src = "/images/sportsbooks/upside_logo.png";
  };

  return (
    <div
      data-tool-stage="landing"
      className="min-h-screen bg-background pb-[calc(3.5rem+env(safe-area-inset-bottom))] md:pb-0"
    >
      <section className="relative overflow-hidden bg-background">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.03)_1px,transparent_0)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_0%,#000_50%,transparent_100%)]" />

        <div className="relative z-10 px-4 pb-10 pt-10 sm:pb-12 sm:pt-16">
          <div className="max-w-5xl mx-auto">
            <motion.div
              {...LANDING_ANIMATIONS.fadeIn}
              className="flex items-center justify-center gap-4 mb-4 sm:mb-6"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="relative flex size-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success/60"></span>
                  <span className="relative inline-flex rounded-full size-1.5 bg-success"></span>
                </span>
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Live
                </span>
              </span>
              <span className="text-sm text-muted-foreground">
                <span className="text-foreground font-semibold">
                  {LANDING_STATS.ACTIVE_USERS_DISPLAY}
                </span>{" "}
                bettors finding edges daily
              </span>
            </motion.div>

            <motion.div
              {...LANDING_ANIMATIONS.fadeInUp}
              className="text-center mb-6 sm:mb-8"
            >
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold mb-4 sm:mb-6 leading-[1.1] tracking-[-0.035em] font-display">
                <span className="text-primary">{content.title}</span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-3 sm:mb-4">
                {content.subtitle}
              </p>
              <p className="hidden text-base text-muted-foreground/80 max-w-xl mx-auto sm:block">
                {content.description}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-8"
            >
              <Button
                asChild
                size="lg"
                className="group w-full max-w-[320px] bg-primary hover:bg-primary/90 text-primary-foreground px-5 py-5 text-base font-semibold rounded-lg transition-all duration-200 sm:w-auto sm:max-w-none sm:px-8 sm:py-6 sm:text-lg"
              >
                <Link
                  href="/pricing"
                  onClick={() =>
                    trackMarketingCtaClick({
                      surface: `${content.analyticsVariant}_landing`,
                      cta: "start_trial",
                      destination: "/pricing",
                    })
                  }
                >
                  <span className="flex items-center gap-2">
                    {content.cta}
                    <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="w-full max-w-[320px] px-5 py-5 text-base font-medium rounded-lg border-border/50 hover:border-primary/50 hover:bg-primary/5 transition-all duration-300 sm:w-auto sm:max-w-none sm:px-8 sm:py-6 sm:text-lg"
              >
                <Link
                  href="/pricing"
                  onClick={() =>
                    trackMarketingCtaClick({
                      surface: `${content.analyticsVariant}_landing`,
                      cta: "pricing",
                      destination: "/pricing",
                    })
                  }
                >
                  Compare plans
                </Link>
              </Button>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-sm text-muted-foreground text-center mb-6 sm:mb-8"
            >
              5-day free trial · Cancel anytime · New subscribers only
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="max-w-5xl mb-12 -mx-4 px-0 sm:mx-auto sm:px-4"
            >
              <div className="relative rounded-none sm:rounded-2xl p-px bg-gradient-to-b from-border via-border/50 to-border/20">
                <div className="absolute -inset-1 rounded-none sm:rounded-2xl bg-white/5 blur-xl" />
                <div className="relative rounded-none sm:rounded-2xl overflow-hidden bg-card">
                  <Image
                    src={content.screenshot}
                    alt={content.screenshotAlt}
                    width={2880}
                    height={1800}
                    className="w-full"
                    priority
                    quality={90}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 90vw, 1024px"
                    onError={handleImageError}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/50">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-semibold tracking-[-0.02em] mb-4">
              Why Bettors Choose{" "}
              <span className="text-primary">This Tool</span>
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Stop guessing. Start betting with a mathematical edge.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {content.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="h-full p-6 rounded-2xl border border-border/60 bg-background shadow-sm hover:shadow-md hover:border-border transition-all">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-muted border border-border/50">
                        <Icon className="size-6 text-foreground" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="p-4"
            >
              <div className="text-3xl font-bold text-primary data-num mb-1">
                {LANDING_STATS.SPORTSBOOKS_SCANNED_DISPLAY}
              </div>
              <div className="text-sm text-muted-foreground">
                Sportsbooks Scanned
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="p-4"
            >
              <div className="text-3xl font-bold text-primary data-num mb-1">
                {LANDING_STATS.ODDS_REFRESH_DISPLAY}
              </div>
              <div className="text-sm text-muted-foreground">Refresh Rate</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="p-4"
            >
              <div className="text-3xl font-bold text-primary data-num mb-1">
                {LANDING_STATS.DAILY_PLAYS_DISPLAY}
              </div>
              <div className="text-sm text-muted-foreground">
                Daily +EV Plays
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="p-4"
            >
              <div className="text-3xl font-bold text-primary data-num mb-1">
                {LANDING_STATS.ACTIVE_USERS_DISPLAY}
              </div>
              <div className="text-sm text-muted-foreground">
                Active Bettors
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/50">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold tracking-[-0.02em] mb-4">
              Ready to Bet Smarter?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Join {LANDING_STATS.ACTIVE_USERS_DISPLAY} bettors who stopped
              guessing and started profiting. Your edge is waiting.
            </p>
            <Button
              asChild
              size="lg"
              className="group bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              <Link
                href="/pricing"
                onClick={() =>
                  trackMarketingCtaClick({
                    surface: `${content.analyticsVariant}_landing_final`,
                    cta: "start_trial",
                    destination: "/pricing",
                  })
                }
              >
                <span className="flex items-center gap-2">
                  Start Your Free Trial
                  <ArrowRight className="size-5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground mt-4">
              5-day free trial · Cancel anytime · Full access
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
